import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AnvaQuote,
  AnvaQuoteWithJob,
  AnvaPackageWithJob,
  AnvaJobStatus,
  AnvaPackage,
} from '@flows/src/types/anva';
import {
  useAnvaQuotesV2Calculate,
  getJobResult,
  AnvaQuotesPayload,
  isAnvaQuote,
  isJobErrorResult,
} from '@flows/src/api/anva-flows/getAnvaQuotesV2';
import useflowsProductBasketStore from '@flows/src/store/zustand/flows-products/flowsProductBasketStore';

// Type guard to check if a result is an AnvaPackage
const isAnvaPackage = (result: unknown): result is AnvaPackage => {
  return (
    typeof result === 'object' &&
    result !== null &&
    'name' in result &&
    'code' in result &&
    'quotes' in result &&
    Array.isArray((result as Record<string, unknown>).quotes)
  );
};

interface JobState {
  jobId: string;
  status: AnvaJobStatus;
  isLoading: boolean;
  error: string | null;
  result: AnvaQuote | AnvaPackage | null; // Package jobs return AnvaPackage, quote jobs return AnvaQuote
  packageCode?: string; // For package quotes
  isStandalone?: boolean; // For standalone insurances
  anvaProductId: string;
  index: number;
}

interface UseBasketCalculateWithPollingReturn {
  calculate: (payload: AnvaQuotesPayload) => Promise<void>;
  isCalculating: boolean;
  isGlobalLoading: boolean;
  jobStates: Record<string, JobState>;
  error: Error | null;
}

export const useBasketCalculateWithPolling = (
  sessionId: string,
): UseBasketCalculateWithPollingReturn => {
  const [jobStates, setJobStates] = useState<Record<string, JobState>>({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pollTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const pollStartTimeRef = useRef<Record<string, number>>({});

  const POLL_INTERVAL = 3000; // 3 seconds
  const POLL_TIMEOUT = 60000; // 60 seconds

  const { mutateAsync: calculateV2 } = useAnvaQuotesV2Calculate();
  const setPackages = useflowsProductBasketStore((state) => state.setPackages);
  const setInsurances = useflowsProductBasketStore(
    (state) => state.setInsurances,
  );
  const setIsBasketValidated = useflowsProductBasketStore(
    (state) => state.setIsBasketValidated,
  );

  const isGlobalLoading = isCalculating && Object.keys(jobStates).length === 0;

  const updateJobState = useCallback(
    (jobId: string, updates: Partial<JobState>) => {
      setJobStates((prev) => ({
        ...prev,
        [jobId]: { ...prev[jobId], ...updates },
      }));
    },
    [],
  );

  const pollJobResult = useCallback(
    async (jobId: string) => {
      try {
        const jobResult = await getJobResult(jobId);

        if (jobResult.status === 'SUCCEEDED' || jobResult.status === 'FAILED') {
          if (jobResult.status === 'SUCCEEDED' && jobResult.result) {
            // Check if the result is a valid AnvaQuote
            if (isAnvaQuote(jobResult.result)) {
              // Check if the result has success: false (API error within successful job)
              if (
                jobResult.result.success === false &&
                jobResult.result.error
              ) {
                updateJobState(jobId, {
                  status: 'FAILED',
                  isLoading: false,
                  error: jobResult.result.error,
                  result: null,
                });
              } else {
                // Extract the index from the result and update the job state
                updateJobState(jobId, {
                  status: 'SUCCEEDED',
                  isLoading: false,
                  result: jobResult.result,
                  error: null,
                  index: jobResult.result.index, // Use the index from the actual result
                });
              }
            } else if (isAnvaPackage(jobResult.result)) {
              // Handle AnvaPackage results
              // Check if the package has success: false (API error within successful job)
              if (
                jobResult.result.success === false &&
                jobResult.result.error
              ) {
                updateJobState(jobId, {
                  status: 'FAILED',
                  isLoading: false,
                  error: jobResult.result.error,
                  result: null,
                });
              } else {
                // Package calculation succeeded
                updateJobState(jobId, {
                  status: 'SUCCEEDED',
                  isLoading: false,
                  result: jobResult.result,
                  error: null,
                  index: -1, // Package jobs don't have a specific index
                });
              }
            } else {
              // Handle job error result - extract actual error details
              let errorMessage = JSON.stringify(jobResult);

              if (jobResult.result && isJobErrorResult(jobResult.result)) {
                errorMessage =
                  jobResult.result.message ||
                  jobResult.result.title ||
                  JSON.stringify(jobResult);
              }

              updateJobState(jobId, {
                status: 'FAILED',
                isLoading: false,
                error: errorMessage,
                result: null,
              });
            }
          } else {
            // Extract error details from job result if available
            let errorMessage = 'NO_RESULT_AVAILABLE';

            if (jobResult.status === 'FAILED') {
              if (jobResult.result && isJobErrorResult(jobResult.result)) {
                errorMessage =
                  jobResult.result.message ||
                  jobResult.result.title ||
                  JSON.stringify(jobResult);
              } else {
                errorMessage = JSON.stringify(jobResult);
              }
            }

            updateJobState(jobId, {
              status: 'FAILED',
              isLoading: false,
              error: errorMessage,
            });
          }
          stopPolling(jobId);
          return true; // Job completed
        }

        // Job is still in progress
        updateJobState(jobId, { status: jobResult.status });
        return false; // Continue polling
      } catch (error) {
        console.error(`Error polling job ${jobId}:`, error);
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        updateJobState(jobId, {
          status: 'FAILED',
          isLoading: false,
          error: errorMessage,
        });
        stopPolling(jobId);
        return true; // Stop polling on error
      }
    },
    [updateJobState],
  );

  const startPolling = useCallback(
    (jobId: string) => {
      if (pollTimeoutRef.current[jobId]) {
        clearTimeout(pollTimeoutRef.current[jobId]);
      }

      pollStartTimeRef.current[jobId] = Date.now();

      const poll = async () => {
        const elapsed = Date.now() - pollStartTimeRef.current[jobId];

        if (elapsed >= POLL_TIMEOUT) {
          updateJobState(jobId, {
            status: 'FAILED',
            isLoading: false,
            error: 'CALCULATION_TIMEOUT',
          });
          stopPolling(jobId);
          return;
        }

        const completed = await pollJobResult(jobId);

        if (!completed) {
          pollTimeoutRef.current[jobId] = setTimeout(poll, POLL_INTERVAL);
        }
      };

      poll();
    },
    [pollJobResult, updateJobState],
  );

  const stopPolling = useCallback((jobId: string) => {
    if (pollTimeoutRef.current[jobId]) {
      clearTimeout(pollTimeoutRef.current[jobId]);
      delete pollTimeoutRef.current[jobId];
    }
    delete pollStartTimeRef.current[jobId];
  }, []);

  // Update basket store when jobs complete
  useEffect(() => {
    const completedJobs = Object.values(jobStates).filter(
      (job) => !job.isLoading && job.result,
    );
    const failedJobs = Object.values(jobStates).filter(
      (job) => !job.isLoading && job.error,
    );

    if (completedJobs.length === 0 && failedJobs.length === 0) return;

    // Group completed results by packages and standalone insurances
    const packageQuotes: Record<string, AnvaQuote[]> = {};
    const standaloneQuotes: AnvaQuote[] = [];

    completedJobs.forEach((job) => {
      if (job.result) {
        if (job.packageCode) {
          if (!packageQuotes[job.packageCode]) {
            packageQuotes[job.packageCode] = [];
          }
          // Handle package-level jobs that return AnvaPackage
          if ('quotes' in job.result && Array.isArray(job.result.quotes)) {
            // This is an AnvaPackage
            packageQuotes[job.packageCode].push(...job.result.quotes);
          } else if ('anva_product_id' in job.result) {
            // This is an AnvaQuote (individual quote in package)
            packageQuotes[job.packageCode].push(job.result);
          }
        } else if (job.isStandalone) {
          // Individual quote jobs should always return single AnvaQuote
          if ('anva_product_id' in job.result) {
            standaloneQuotes.push(job.result);
          }
        }
      }
    });

    // Update packages in store with completed quotes
    const currentPackages = useflowsProductBasketStore.getState().packages;
    const updatedPackages = currentPackages.map((pkg) => {
      const completedPackageQuotes = packageQuotes[pkg.code] || [];

      // Find the package-level job result for this package
      const packageJobResult = completedJobs.find(
        (job) => job.packageCode === pkg.code && isAnvaPackage(job.result),
      );

      if (completedPackageQuotes.length > 0) {
        // Create a copy of completed quotes for fallback matching to avoid double-matching
        const remainingCompletedQuotes = [...completedPackageQuotes];

        const updatedPackage = {
          ...pkg,
          quotes: pkg.quotes.map((quote) => {
            // First try to match by both anva_product_id and index
            const updatedQuote = completedPackageQuotes.find(
              (completed) =>
                completed.anva_product_id === quote.anva_product_id &&
                completed.index === quote.index,
            );

            if (updatedQuote) {
              // Remove from remaining quotes to avoid double-matching
              const index = remainingCompletedQuotes.findIndex(
                (q) =>
                  q.anva_product_id === updatedQuote.anva_product_id &&
                  q.index === updatedQuote.index,
              );
              if (index > -1) remainingCompletedQuotes.splice(index, 1);
              return updatedQuote;
            }

            // If no exact match found, try to match by anva_product_id only from remaining quotes
            // This handles cases where the index might have changed between calculations
            const fallbackQuote = remainingCompletedQuotes.find(
              (completed) =>
                completed.anva_product_id === quote.anva_product_id,
            );

            if (fallbackQuote) {
              // Remove from remaining quotes to avoid double-matching
              const index = remainingCompletedQuotes.findIndex(
                (q) =>
                  q.anva_product_id === fallbackQuote.anva_product_id &&
                  q.index === fallbackQuote.index,
              );
              if (index > -1) remainingCompletedQuotes.splice(index, 1);
              return fallbackQuote;
            }

            return quote;
          }),
        };

        // Update package-level properties from AnvaPackage result if available
        if (packageJobResult && isAnvaPackage(packageJobResult.result)) {
          const anvaPackageResult = packageJobResult.result;
          return {
            ...updatedPackage,
            total_premium: anvaPackageResult.total_premium,
            discount: anvaPackageResult.discount,
            discount_percentage: anvaPackageResult.discount_percentage,
          };
        }

        return updatedPackage;
      }

      // Even if no quotes, still update package-level properties if we have a package result
      if (packageJobResult && isAnvaPackage(packageJobResult.result)) {
        const anvaPackageResult = packageJobResult.result;
        return {
          ...pkg,
          total_premium: anvaPackageResult.total_premium,
          discount: anvaPackageResult.discount,
          discount_percentage: anvaPackageResult.discount_percentage,
        };
      }

      return pkg;
    });

    // Update standalone insurances
    const currentInsurances = useflowsProductBasketStore.getState().insurances;
    // Create a copy of completed quotes for fallback matching to avoid double-matching
    const remainingStandaloneQuotes = [...standaloneQuotes];

    const updatedInsurances = currentInsurances.map((insurance) => {
      // First try to match by both anva_product_id and index
      const updatedInsurance = standaloneQuotes.find(
        (completed) =>
          completed.anva_product_id === insurance.anva_product_id &&
          completed.index === insurance.index,
      );

      if (updatedInsurance) {
        // Remove from remaining quotes to avoid double-matching
        const index = remainingStandaloneQuotes.findIndex(
          (q) =>
            q.anva_product_id === updatedInsurance.anva_product_id &&
            q.index === updatedInsurance.index,
        );
        if (index > -1) remainingStandaloneQuotes.splice(index, 1);
        return updatedInsurance;
      }

      // If no exact match found, try to match by anva_product_id only from remaining quotes
      // This handles cases where the index might have changed between calculations
      const fallbackInsurance = remainingStandaloneQuotes.find(
        (completed) => completed.anva_product_id === insurance.anva_product_id,
      );

      if (fallbackInsurance) {
        // Remove from remaining quotes to avoid double-matching
        const index = remainingStandaloneQuotes.findIndex(
          (q) =>
            q.anva_product_id === fallbackInsurance.anva_product_id &&
            q.index === fallbackInsurance.index,
        );
        if (index > -1) remainingStandaloneQuotes.splice(index, 1);
        return fallbackInsurance;
      }

      return insurance;
    });

    // Only update store if we have changes
    if (Object.keys(packageQuotes).length > 0) {
      setPackages(updatedPackages);
    }
    if (standaloneQuotes.length > 0) {
      setInsurances(updatedInsurances);
    }

    // Mark basket as validated when all jobs are done (success or failure)
    const allJobsDone = Object.values(jobStates).every((job) => !job.isLoading);
    if (allJobsDone && Object.keys(jobStates).length > 0) {
      setIsBasketValidated(true);
      setIsCalculating(false);

      // Clear only successful job states after a few seconds, keep error states persistent
      setTimeout(() => {
        setJobStates((prevStates) => {
          const failedStates: Record<string, JobState> = {};

          // Keep only the failed job states
          Object.entries(prevStates).forEach(([jobId, jobState]) => {
            if (jobState.error || jobState.status === 'FAILED') {
              failedStates[jobId] = jobState;
            }
          });

          return failedStates;
        });
      }, 3000);
    }
  }, [jobStates, setPackages, setInsurances, setIsBasketValidated]);

  const calculate = useCallback(
    async (payload: AnvaQuotesPayload) => {
      try {
        setError(null);
        setIsCalculating(true);
        setIsBasketValidated(false);

        // Clear previous job states
        const currentJobStates = Object.keys(jobStates);
        currentJobStates.forEach(stopPolling);
        setJobStates({});

        const responseData = await calculateV2({
          sessionId,
          payload,
        });

        // Initialize job states for each quote with job info
        const newJobStates: Record<string, JobState> = {};

        // Handle individual quote jobs (from responseData.quotes)
        responseData.quotes.forEach((quoteWithJob: AnvaQuoteWithJob) => {
          const jobId = quoteWithJob.job.job_id;

          // All quotes in responseData.quotes are standalone insurance jobs
          newJobStates[jobId] = {
            jobId,
            status: quoteWithJob.job.status,
            isLoading: true,
            error: null,
            result: null,
            packageCode: undefined,
            isStandalone: true,
            anvaProductId: quoteWithJob.anva_product_id,
            index: 0, // Standalone quotes don't have index in this context - will be updated from result
          };
        });

        // Handle package-level jobs
        if (responseData.packages) {
          responseData.packages.forEach(
            (packageWithJob: AnvaPackageWithJob) => {
              const jobId = packageWithJob.job.job_id;
              const packageCode = packageWithJob.code;

              newJobStates[jobId] = {
                jobId,
                status: packageWithJob.job.status,
                isLoading: true,
                error: null,
                result: null,
                packageCode,
                isStandalone: false,
                anvaProductId: '', // Package jobs don't have a specific product ID
                index: -1, // Temporary placeholder, package jobs may have multiple quotes with different indices
              };
            },
          );
        }

        setJobStates(newJobStates);

        // Start polling for each job
        Object.keys(newJobStates).forEach((jobId) => {
          startPolling(jobId);
        });
      } catch (error) {
        console.error('Basket calculation error:', error);
        setError(error as Error);
        setIsCalculating(false);
      }
    },
    [sessionId, calculateV2, stopPolling, startPolling, setIsBasketValidated],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.keys(pollTimeoutRef.current).forEach((jobId) => {
        stopPolling(jobId);
      });
    };
  }, [stopPolling]);

  return {
    calculate,
    isCalculating,
    isGlobalLoading,
    jobStates,
    error,
  };
};
